import { formatInTimeZone } from 'date-fns-tz';
import { Maximize2, Minimize2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
    OUTREACH_ANALYTICS_TIMEZONE,
    OUTREACH_REFRESH_INTERVAL_MS,
} from '@apps/web/components/admin/outreach_observability/constants';
import {
    TvDailyLineChart,
    type TvDailySeries,
} from '@apps/web/components/admin/outreach_observability/tv_daily_line_chart';
import { TvLatencyBoard } from '@apps/web/components/admin/outreach_observability/tv_latency_board';
import { Button } from '@apps/web/components/ui/button';
import { useAdminOutreachTv } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type TvDailyMetricsBucket,
    type TvDailyMetricsSection,
} from '@libraries/typescript/api_client';

// The chart legend sits above the fixed-height plot area, so the measured
// column height is reduced by that chrome to avoid overflow.
const CHART_CHROME_PX = 40;

// Distinct dark-theme line colors: warm/amber for urgent, cyan for regular.
const URGENT_COLOR = 'var(--chart-3)';
const REGULAR_COLOR = 'var(--chart-1)';

// In fullscreen the wallboard rotates between the latency and daily-metrics
// boards, holding each for this long.
const TV_ROTATION_INTERVAL_MS = 20_000;

type TvTabProps = {
    readonly organizationIds: Array<string>;
};

// Compact form for the chart axis/tooltip, where "2m 20s" wraps the gutter.
function formatWaitMinutes(ms: number): string {
    if (ms < 60_000) {
        return `${Math.round(ms / 1000)}s`;
    }
    return `${(ms / 60_000).toFixed(1)}m`;
}

function formatCost(usd: number): string {
    return `$${usd.toFixed(2)}`;
}

function formatPct(fraction: number): string {
    return `${Math.round(fraction * 100)}%`;
}

function toSeries(
    label: string,
    color: string,
    buckets: Array<TvDailyMetricsBucket>,
    select: (bucket: TvDailyMetricsBucket) => number | null,
): TvDailySeries {
    return {
        label,
        color,
        points: buckets.map((bucket) => ({
            day: bucket.day,
            value: select(bucket),
            runCount: bucket.run_count,
        })),
    };
}

export function TvTab({ organizationIds }: TvTabProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const rotationIntervalRef = useRef<number | null>(null);
    const [showLatencyBoard, setShowLatencyBoard] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const { data } = useAdminOutreachTv(
        { organizationIds },
        { refreshIntervalMs: OUTREACH_REFRESH_INTERVAL_MS },
    );

    // Only in fullscreen: start on the latency board, then rotate every 20s.
    // Exiting fullscreen stops the rotation and returns to daily metrics.
    useEffect(() => {
        const clearRotation = () => {
            if (rotationIntervalRef.current !== null) {
                window.clearInterval(rotationIntervalRef.current);
                rotationIntervalRef.current = null;
            }
        };
        const handleFullscreenChange = () => {
            const isContainerFullscreen =
                document.fullscreenElement === containerRef.current;
            setIsFullscreen(isContainerFullscreen);
            clearRotation();
            if (isContainerFullscreen) {
                setShowLatencyBoard(true);
                rotationIntervalRef.current = window.setInterval(() => {
                    setShowLatencyBoard((previous) => !previous);
                }, TV_ROTATION_INTERVAL_MS);
            } else {
                setShowLatencyBoard(false);
            }
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener(
                'fullscreenchange',
                handleFullscreenChange,
            );
            clearRotation();
        };
    }, []);

    // Requesting fullscreen for the element that is already fullscreen is
    // refused by WebKit, so the press that leaves the wallboard has to exit
    // instead. A refused request leaves the board as it is; the rejection is
    // caught because an unhandled one is reported as a page error.
    const handleFullscreen = () => {
        const container = containerRef.current;
        if (container === null) {
            return;
        }
        const transition =
            document.fullscreenElement === container
                ? document.exitFullscreen()
                : container.requestFullscreen();
        void transition.catch(() => {});
    };

    return (
        <div
            ref={containerRef}
            data-testid="tv-tab-container"
            className="dark relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-zinc-950 p-8 text-zinc-50"
        >
            <div className="absolute top-6 right-6 flex items-center gap-3 text-sm text-zinc-500">
                {data ? (
                    <span className="tabular-nums">
                        as of{' '}
                        {formatInTimeZone(
                            new Date(data.as_of),
                            OUTREACH_ANALYTICS_TIMEZONE,
                            'h:mm a',
                        )}{' '}
                        ET
                    </span>
                ) : null}
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleFullscreen}
                    className="border-zinc-700 bg-zinc-900 text-zinc-200 hover:bg-zinc-800 hover:text-zinc-50"
                >
                    {isFullscreen ? <Minimize2 /> : <Maximize2 />}
                    {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                </Button>
            </div>
            {data === undefined ? (
                <TvSkeleton />
            ) : showLatencyBoard ? (
                <TvLatencyBoard data={data} />
            ) : data.daily_metrics ? (
                <TvBoard metrics={data.daily_metrics} />
            ) : (
                <TvSkeleton />
            )}
        </div>
    );
}

// One column: title over a ResizeObserver-measured daily chart that fills
// whatever vertical space the wallboard leaves it.
function MetricColumn({
    title,
    urgent,
    regular,
    valueFormatter,
    yDomain,
}: {
    readonly title: string;
    readonly urgent: TvDailySeries;
    readonly regular: TvDailySeries;
    readonly valueFormatter: (value: number) => string;
    readonly yDomain?: [number, number];
}) {
    const chartAreaRef = useRef<HTMLDivElement | null>(null);
    const [chartHeight, setChartHeight] = useState(240);
    useEffect(() => {
        const element = chartAreaRef.current;
        if (element === null) {
            return;
        }
        const observer = new ResizeObserver((entries) => {
            const height = entries[0]?.contentRect.height;
            if (height !== undefined && height > 0) {
                setChartHeight(
                    Math.max(160, Math.floor(height) - CHART_CHROME_PX),
                );
            }
        });
        observer.observe(element);
        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <div className="flex min-h-0 min-w-0 flex-col gap-4">
            <span className="text-base font-medium tracking-wide text-zinc-400 uppercase">
                {title}
            </span>
            <div ref={chartAreaRef} className="min-h-0 flex-1">
                <TvDailyLineChart
                    urgent={urgent}
                    regular={regular}
                    valueFormatter={valueFormatter}
                    height={chartHeight}
                    yDomain={yDomain}
                />
            </div>
        </div>
    );
}

function TvBoard({ metrics }: { readonly metrics: TvDailyMetricsSection }) {
    const speedUrgent = toSeries(
        'Urgent',
        URGENT_COLOR,
        metrics.urgent,
        (bucket) => bucket.median_time_to_first_response_ms,
    );
    const speedRegular = toSeries(
        'Normal',
        REGULAR_COLOR,
        metrics.regular,
        (bucket) => bucket.median_time_to_first_response_ms,
    );
    const costUrgent = toSeries(
        'Urgent',
        URGENT_COLOR,
        metrics.urgent,
        (bucket) => bucket.avg_cost_usd,
    );
    const costRegular = toSeries(
        'Normal',
        REGULAR_COLOR,
        metrics.regular,
        (bucket) => bucket.avg_cost_usd,
    );
    const qualityUrgent = toSeries(
        'Urgent',
        URGENT_COLOR,
        metrics.urgent,
        (bucket) => bucket.pct_great,
    );
    const qualityRegular = toSeries(
        'Normal',
        REGULAR_COLOR,
        metrics.regular,
        (bucket) => bucket.pct_great,
    );
    return (
        <div className="grid min-h-0 flex-1 grid-cols-3 gap-10">
            <MetricColumn
                title="Speed — median latency"
                urgent={speedUrgent}
                regular={speedRegular}
                valueFormatter={formatWaitMinutes}
            />
            <MetricColumn
                title="Cost — avg $/run"
                urgent={costUrgent}
                regular={costRegular}
                valueFormatter={formatCost}
            />
            <MetricColumn
                title="Quality — % great"
                urgent={qualityUrgent}
                regular={qualityRegular}
                valueFormatter={formatPct}
                yDomain={[0, 1]}
            />
        </div>
    );
}

function TvSkeleton() {
    return (
        <div className="grid min-h-0 flex-1 animate-pulse grid-cols-3 gap-10">
            {[0, 1, 2].map((column) => (
                <div key={column} className="flex min-h-0 flex-col gap-4">
                    <div className="h-6 w-64 rounded bg-zinc-800" />
                    <div className="min-h-0 flex-1 rounded bg-zinc-800/50" />
                </div>
            ))}
        </div>
    );
}
